import { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { Autocomplete, Box, Chip, IconButton, TextField, Tooltip } from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import TreeViewSearch from "./AddressTreeViewSearch";

const stopPropagation = event => event?.stopPropagation();

const AddressTreeViewSelect = ({ treeData, addressOptions, onSelect, selectedAddressField }) => {

  const handleSelect = (node) => {

    //The old component was an regular dropdown and it 
    //communicated by a regular event
    //with the selected value in e.target.value
    var e = { target: { value: node.id } };
    onSelect(e);

    if (!node.children || node.children.length === 0) {
      setOpen(false);
    }
    else {
      // It's a group � set auto-close timeout
      groupClickTimeoutRef.current = setTimeout(() => {
        setOpen(false);
      }, 2500);
    }

  }

  const [hasOpened, setHasOpened] = useState(false);
  const [open, setOpen] = useState(false);

  const groupClickTimeoutRef = useRef(null);

  const [manualExpand, setManualExpand] = useState([]);

  const [selectedNode, setSelectedNode] = useState();
  const [showPersons, setShowPersons] = useState(false);

  const [groupsList, setGroupsList] = useState([]);
  const [inputText, setInputText] = useState("");
  const intl = useIntl();
  // Helper to find a node by ID
  const findNodeById = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    const containsGroup = treeData.some(node => node.type === "GROUP");
    if (!containsGroup) {
      setShowPersons(true);
    }
  }, [treeData]);

  useEffect(() => {
    const collectGroupIds = (node) => {
      setGroupsList(prev => [...prev, node.id]);
      node.children?.forEach(collectGroupIds);
    };
    treeData.forEach(collectGroupIds);

    var node = findNodeById(treeData, selectedAddressField)
    setSelectedNode(node);

  }, [treeData, addressOptions, selectedNode, selectedAddressField]);


  useEffect(() => {
    return () => {
      if (groupClickTimeoutRef.current) {
        clearTimeout(groupClickTimeoutRef.current);
      }
    };
  }, []);

  const removeChip = () => {
    setSelectedNode(null);
    onSelect({ target: { value: null } });
  };

  const renderChips = () => {

    if (!selectedNode) return null;

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        <Chip
          key={selectedNode.id}
          label={selectedNode.label}
          onDelete={removeChip}
          onMouseDown={stopPropagation} // prevents Autocomplete dropdown toggle
        />
      </Box>
    );
  };




  return (
    <Autocomplete
      sx={{ padding: 0 }}
      options={[treeData]}
      value={selectedNode ? findNodeById(treeData, selectedNode?.id) : null}

      open={open}
      onOpen={() => {

        if (groupClickTimeoutRef.current) {
          clearTimeout(groupClickTimeoutRef.current);
        }

        setHasOpened(true);
        setOpen(true);
      }}
      onClose={() => setOpen(false)}

      onChange={(event, newValue) => {
        if (newValue) {
          setSelectedNode(newValue/*.id*/);
        } else {
          setSelectedNode(null);
        }
        setInputText("");
      }}
      inputValue={inputText}
      onInputChange={(event, newInput) => {
        if (groupClickTimeoutRef.current) {
          clearTimeout(groupClickTimeoutRef.current);
        }

        if (event && event.type === "change") {
          setInputText(newInput);
        }
      }}
      filterOptions={(x) => x}
      onBlur={() => setInputText("")}
      clearOnEscape={false}
      disableClearable
      disableCloseOnSelect
      getOptionLabel={(option) => option.label || ""}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderTags={() => renderChips()}
      renderOption={() => (
        <TreeViewSearch
          showPersons={showPersons}
          data={treeData}
          selectedNode={selectedNode}
          setSelectedNode={handleSelect}
          inputText={inputText}
          groupsList={groupsList}
          hasOpened={hasOpened}
          manualExpand={manualExpand}
          setManualExpand={setManualExpand}
        />
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={intl.formatMessage({ id: "cart.selectAddress" })}
          InputLabelProps={{ shrink: true }}
          placeholder={intl.formatMessage({ id: "cart.searchOrSelectAddress" })}
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '0 0 0 4px',
              minHeight: '32px',
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedNode ? (
              <Chip
                key={selectedNode.id}
                label={selectedNode.label}
                onDelete={removeChip}
                onMouseDown={stopPropagation}
                sx={{ mr: 1 }}
              />
            ) : null,
            endAdornment: (
              <Tooltip arrow title={intl.formatMessage({ id: "cart.showHideOtherUsersAddresses" })} placement="top">
                <IconButton onClick={() => setShowPersons(!showPersons)}>{showPersons ? <PersonIcon /> : <PersonOffIcon />}</IconButton>{params.InputProps.endAdornment}
              </Tooltip>),
          }}
        />
      )}
    />
  );
};

export default AddressTreeViewSelect;